<script>
  import { setContext } from "svelte";
  import { slide, fade } from "svelte/transition";
  import { router } from "@inertiajs/svelte";
  import { page } from "@inertiajs/svelte";

  import LogoText from "../components/LogoText.svelte";

  let { content, currentUser, container = true } = $props();
  let sidebarOpen = $state(true);

  // contexts
  setContext("currentUser", currentUser);

  // Navigation items
  const navigation = [
    {
      name: "Dashboard",
      icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
      href: "/dashboard",
      current: false,
    },
    {
      name: "Settings",
      icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
      href: "/dashboard/settings",
      current: false,
    },
  ];

  // Update current navigation item based on current URL
  $effect.pre(() => {
    const currentPath = $page.url;

    navigation.forEach((item) => {
      item.current = currentPath === item.href;
    });
  });

  function goToItem(event, item) {
    event.preventDefault();
    router.get(item.href);
  }

  function toggleSidebar() {
    sidebarOpen = !sidebarOpen;
  }
</script>

<div class="min-h-screen bg-gray-50">
  <!-- Static sidebar for desktop -->
  <div class="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:z-50">
    <div
      class={`transition-all duration-300 ease-in-out flex flex-col ${sidebarOpen ? "w-64" : "w-20"} h-full bg-white shadow-xl overflow-y-auto border-r border-gray-200`}
    >
      <div class="flex items-center justify-between flex-shrink-0 px-4 py-5">
        {#if sidebarOpen}
          <LogoText size="md" color="indigo" />
        {:else}
          <div class="mx-auto">
            <div
              class="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center"
            >
              <span class="text-white font-bold text-lg">S</span>
            </div>
          </div>
        {/if}
        <button
          class="text-gray-500 hover:text-indigo-600 focus:outline-none transition-colors duration-200"
          onclick={toggleSidebar}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d={sidebarOpen
                ? "M11 19l-7-7 7-7m8 14l-7-7 7-7"
                : "M13 5l7 7-7 7M5 5l7 7-7 7"}
            />
          </svg>
        </button>
      </div>

      <div class="flex-1 flex flex-col pt-5 pb-4">
        <nav class="mt-5 flex-1 px-3 space-y-2">
          {#each navigation as item}
            <a
              onclick={(event) => goToItem(event, item)}
              href={item.href}
              class={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${item.current ? "bg-indigo-100 text-indigo-800 shadow-sm" : "text-gray-700 hover:bg-gray-100 hover:text-indigo-700"}`}
            >
              <svg
                class={`${sidebarOpen ? "mr-3" : "mx-auto"} h-6 w-6 transition-colors duration-200 ${item.current ? "text-indigo-600" : "text-gray-500 group-hover:text-indigo-600"}`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d={item.icon}
                />
              </svg>
              {#if sidebarOpen}
                <span>{item.name}</span>
              {/if}
            </a>
          {/each}
        </nav>
      </div>

      <div class="flex-shrink-0 flex border-t border-gray-200 p-4">
        <a href="/profile" class="flex-shrink-0 w-full group block">
          {#if sidebarOpen}
            <div class="flex items-center">
              <div>
                <div
                  class="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold shadow-sm"
                >
                  {currentUser?.name?.charAt(0) || "U"}
                </div>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-gray-800">
                  {currentUser?.name || "User"}
                </p>
                <p
                  class="text-xs font-medium text-gray-500 group-hover:text-indigo-600 transition-colors duration-200"
                >
                  View profile
                </p>
              </div>
            </div>
          {:else}
            <div class="flex items-center justify-center">
              <div
                class="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold shadow-sm"
              >
                {currentUser?.name?.charAt(0) || "U"}
              </div>
            </div>
          {/if}
        </a>
      </div>
    </div>
  </div>

  <!-- Main content -->
  <div
    class={`transition-all duration-300 ease-in-out ${sidebarOpen ? "lg:pl-64" : "lg:pl-20"} flex flex-col flex-1`}
  >
    <!-- Mobile header -->
    <header class="bg-white shadow-sm lg:hidden sticky top-0 z-30">
      <div class="flex items-center justify-center h-16 px-4">
        <LogoText size="md" color="indigo" />
      </div>
    </header>

    <!-- Main content area with bottom padding for mobile tabs -->
    <main class="flex-1 pb-20 lg:pb-0">
      <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          {#if container}
            <div
              class="bg-white shadow-md rounded-xl p-6 border border-gray-100"
            >
              {@render content({ currentUser })}
            </div>
          {:else}
            {@render content({ currentUser })}
          {/if}
        </div>
      </div>
    </main>
  </div>

  <!-- Mobile bottom tab bar -->
  <div
    class="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg"
  >
    <div class="flex justify-around items-center py-1 px-2">
      {#each navigation as item}
        <a
          href={item.href}
          onclick={(event) => goToItem(event, item)}
          class={`flex flex-col items-center justify-center py-1 px-2 min-w-0 flex-1 text-center transition-all duration-200 ${item.current ? "text-indigo-600" : "text-gray-500"}`}
        >
          <div
            class={`transition-all duration-200 ${item.current ? "bg-indigo-50 rounded-md p-0.5" : ""}`}
          >
            <svg
              class={`h-5 w-5 transition-colors duration-200 ${item.current ? "text-indigo-600" : "text-gray-500"}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d={item.icon}
              />
            </svg>
          </div>
          <span
            class={`text-xs font-medium transition-colors duration-200 ${item.current ? "text-indigo-600" : "text-gray-500"}`}
          >
            {item.name}
          </span>
        </a>
      {/each}

      <!-- Profile tab -->
      <a
        href="/profile"
        class="flex flex-col items-center justify-center py-1 px-2 min-w-0 flex-1 text-center transition-all duration-200 text-gray-500"
      >
        <div
          class="h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center"
        >
          <span class="text-indigo-700 font-bold text-xs">
            {currentUser?.name?.charAt(0) || "U"}
          </span>
        </div>
        <span class="text-xs font-medium text-gray-500"> Profile </span>
      </a>
    </div>
  </div>
</div>
